import { apiErrorMessage, customFetch } from '@agos/api-client'
import {
  BLOCK_KINDS,
  BLOCKED_CONTACTS_KEY,
  BLOCKED_CONTACTS_PATH,
  blockedContactPath,
  blockedContactsOf,
  blockKindLabel,
  blockSource,
  formatDateTime,
  ipBlockCaveat,
  T,
  tt,
  type BlockedContact,
  type BlockKind,
} from '@agos/shared'
import {
  BUTTON_PRIMARY,
  ConfirmDialog,
  DataTable,
  PageHeader,
  ReadFailedBlock,
  Spinner,
  cn,
  toast,
  type Column,
} from '@agos/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, Plus, ShieldOff } from 'lucide-react'
import { useState, type FormEvent } from 'react'

const FIELD = 'w-full px-3 py-2 rounded-lg border border-border bg-background text-sm'

/**
 * Marketing → *Zablokované* - `FRONTEND.md` §9.787, on BACKEND.md §10A.439, owner O140: *"Možnosť zablokovať
 * email/telefonné číslo - či IP adresu, odkiaľ prišla objednávka - proti zlým objednávkam"*.
 *
 * Every block with where it came from and how many web orders it has stopped; one added by hand; one taken off.
 * Blocks also arrive from an order (the archive's *Zablokovať*) and from a customer (the drawer in *Zákazníci*).
 */
export function BlockedContactsPage() {
  const queryClient = useQueryClient()
  const read = useQuery({
    queryKey: BLOCKED_CONTACTS_KEY,
    queryFn: () => customFetch<unknown>(BLOCKED_CONTACTS_PATH),
  })
  const blocks = blockedContactsOf(read.data)
  const [removing, setRemoving] = useState<BlockedContact | null>(null)
  const refresh = () => void queryClient.invalidateQueries({ queryKey: BLOCKED_CONTACTS_KEY })

  const remove = async (block: BlockedContact) => {
    try {
      await customFetch<unknown>(blockedContactPath(block.id), { method: 'DELETE' })
      toast({ title: tt('Odblokované'), description: `${blockKindLabel(block.kind)} ${block.shown}` })
      refresh()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Odblokovať sa nepodarilo'),
        description: apiErrorMessage(caught, tt('Skúste to znova.')),
      })
    }
    setRemoving(null)
  }

  const columns: Column<BlockedContact>[] = [
    {
      key: 'what',
      header: tt('Čo'),
      render: (block) => (
        <span className="font-semibold" data-blocked-value={block.id}>
          <span className="text-muted-foreground font-normal">{blockKindLabel(block.kind)}</span> {block.shown}
        </span>
      ),
      sortable: true,
      sortValue: (block) => block.shown,
    },
    { key: 'reason', header: tt('Dôvod'), render: (block) => block.reason ?? '-' },
    { key: 'source', header: tt('Odkiaľ'), render: (block) => blockSource(block) },
    {
      key: 'created',
      header: tt('Zablokované'),
      render: (block) => (
        <span className="text-xs">
          {block.createdAt === null ? '-' : formatDateTime(block.createdAt)}
          {block.createdBy !== null && <span className="block text-muted-foreground">{block.createdBy}</span>}
        </span>
      ),
      sortable: true,
      sortValue: (block) => block.createdAt,
    },
    {
      key: 'attempts',
      header: tt('Zastavené objednávky'),
      render: (block) => (
        <span className="text-xs" data-blocked-attempts={block.attempts}>
          {block.attempts}
          {block.lastAttemptAt !== null && (
            <span className="block text-muted-foreground">
              <T>naposledy</T> {formatDateTime(block.lastAttemptAt)}
            </span>
          )}
        </span>
      ),
      sortable: true,
      sortValue: (block) => block.attempts,
    },
    {
      key: 'actions',
      header: '',
      render: (block) => (
        <button
          type="button"
          data-unblock={block.id}
          onClick={() => setRemoving(block)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-border text-xs font-semibold"
        >
          <ShieldOff className="w-3.5 h-3.5" /> <T>Odblokovať</T>
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Zablokované')}
        description={tt(
          'Telefóny, e-maily a IP adresy, z ktorých neprijmeme objednávku z webu. Zablokovaný zákazník sa ani neprihlási.',
        )}
        icon={<Ban className="w-5 h-5" />}
      />

      <AddBlock onAdded={refresh} />

      {read.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : blocks === null ? (
        <ReadFailedBlock
          marker="read"
          of="blocked-contacts"
          title={tt('Zoznam zablokovaných sa nepodarilo načítať')}
          onRetry={() => void read.refetch()}
        >
          <T>Preto tu nie je ani prázdny zoznam - nevieme, kto je zablokovaný.</T>
        </ReadFailedBlock>
      ) : (
        <DataTable
          columns={columns}
          data={blocks}
          rowKey={(block) => block.id}
          rowAttributes={(block) => ({ 'data-blocked-contact': block.id })}
          emptyMessage={tt('Nikto nie je zablokovaný.')}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing !== null) void remove(removing)
        }}
        title={tt('Odblokovať?')}
        description={
          removing === null
            ? ''
            : `${blockKindLabel(removing.kind)} ${removing.shown} - ${tt('objednávky z neho prijmeme znova.')}`
        }
        confirmLabel={tt('Odblokovať')}
        cancelLabel={tt('Zrušiť')}
      />
    </div>
  )
}

/** One block typed by hand. */
function AddBlock({ onAdded }: { onAdded: () => void }) {
  const [kind, setKind] = useState<BlockKind>('phone')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (value.trim() === '') return
    setSaving(true)
    try {
      await customFetch<unknown>(BLOCKED_CONTACTS_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, value: value.trim(), reason: reason.trim() === '' ? null : reason.trim() }),
      })
      toast({ title: tt('Zablokované'), description: `${blockKindLabel(kind)} ${value.trim()}` })
      setValue('')
      setReason('')
      onAdded()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Zablokovať sa nepodarilo'),
        description: apiErrorMessage(caught, tt('Skúste to znova.')),
      })
    }
    setSaving(false)
  }

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="rounded-xl border border-border bg-card p-4 space-y-3"
      data-add-block
    >
      <div className="grid gap-3 sm:grid-cols-[10rem_1fr_1fr_auto] items-end">
        <label className="block">
          <span className="block text-xs font-medium mb-1">
            <T>Čo blokovať</T>
          </span>
          <select
            value={kind}
            onChange={(event) => setKind(event.target.value as BlockKind)}
            data-add-block-kind
            className={FIELD}
          >
            {BLOCK_KINDS.map((one) => (
              <option key={one} value={one}>
                {blockKindLabel(one)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1">{blockKindLabel(kind)}</span>
          <input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            data-add-block-value
            inputMode={kind === 'phone' ? 'tel' : kind === 'email' ? 'email' : 'text'}
            className={FIELD}
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1">
            <T>Dôvod (nepovinný)</T>
          </span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            data-add-block-reason
            className={FIELD}
          />
        </label>
        <button
          type="submit"
          disabled={saving || value.trim() === ''}
          data-add-block-submit
          className={cn('flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm', BUTTON_PRIMARY)}
        >
          <Plus className="w-4 h-4" /> <T>Zablokovať</T>
        </button>
      </div>
      {kind === 'ip' && (
        <p className="text-xs text-amber-500" data-ip-caveat>
          {ipBlockCaveat()}
        </p>
      )}
    </form>
  )
}
